<app-help-header></app-help-header>
<main>
  <div class="content">
    <div class="container help-content">
      <div class="row">
        <div class="col-md-3">
          <app-help-menu class="hidden-sm"></app-help-menu>
        </div>
        <div class="col-md-9">
          <section #h_SmartPowerReport [@fadeInAnimation]>
            <h3 translate id="item-3-2">Smart Power Report</h3>
            <div translate class="sum">
              Smart Power Report provides a comprehensive overview of your UPS fleet's performance, health, capacity, and estimated consumption footprint. This periodic report helps IT administrators identify potential infrastructure risks, plan for capacity expansion, and quantify the active protection provided by CyberPower UPS devices.
            </div>
            <div class="img">
              <img class="img-fluid" src="../../assets/images/help/help-smartPowerReport-0.png">
            </div>
            <div class="list" #h_SmartPowerReport_Supported>
              <h5 translate>Supported Devices Scope</h5>
              <div class="section">
                <ul>
                  <li translate>Please note that the Smart Power Report is currently optimized exclusively for Single-Phase UPS devices. To ensure the highest accuracy of power protection metrics, other equipment types—including 3-Phase UPS, ATS, and PDUs—are excluded from this report's calculations. The total device count shown in this report may differ from the total connected devices in your PowerPanel Cloud account.</li>
                </ul>
              </div>
            </div>
            <div class="list" #h_SmartPowerReport_Summary>
              <h5 translate>UPS Protection Summary</h5>
              <div class="section">
                <ul>
                  <li translate class="style_none">This section highlights the active defense your UPS devices provided against grid instability.</li>
                </ul>
                <ol>
                  <li>
                    <strong translate class="strong black">Power Threats Blocked by UPS</strong><strong>:</strong><br>
                    <span translate>The total number of power anomaly events where the UPS actively intervened (e.g., Utility Power Failed) to protect your connected equipment.</span>
                  </li>
                  <li>
                    <strong translate class="strong black">Threats blocked vs. last week</strong><strong>:</strong><br>
                    <span translate>The increase or decrease in blocked threats compared to the previous reporting period.</span>
                  </li>
                </ol>
              </div>
              <div class="img">
                <img class="img-fluid" src="../../assets/images/help/help-smartPowerReport-1.png">
              </div>
            </div>
            <div class="list" #h_SmartPowerReport_Insights>
              <h5 translate>Power Anomaly Insights</h5>
              <div class="section">
                <ul>
                  <li translate class="style_none">Identifies how widespread power issues are across your deployed fleet.</li>
                </ul>
                <ol>
                  <li>
                    <strong translate class="strong black">Device With Power Events</strong><strong>:</strong><br>
                    <span translate>The number of individual UPS units out of your supported fleet that experienced at least one power anomaly event.</span>
                  </li>
                  <li>
                    <strong translate class="strong black">Chart</strong><strong>:</strong><br>
                    <span translate>Illustrates the average power events per device.</span>
                  </li>
                </ol>
              </div>
              <div class="img">
                <img class="img-fluid" src="../../assets/images/help/help-smartPowerReport-2.png">
              </div>
            </div>
            <div class="list" #h_SmartPowerReport_Wellness>
              <h5 translate>System Wellness</h5>
              <div class="section">
                <ul>
                  <li translate class="style_none">Monitors the operational health and connectivity reliability of your devices.</li>
                </ul>
                <ol>
                  <li>
                    <strong translate class="strong black">Time-At-Risk</strong><strong>:</strong><br>
                    <span translate>The percentage of time your devices spent in Critical or Warning states. (Excludes Offline periods for accurate hardware assessment).</span>
                  </li>
                  <li>
                    <strong translate class="strong black">Monitoring Coverage</strong><strong>:</strong><br>
                    <span translate>The percentage of time your devices were successfully connected and reporting data.</span>
                  </li>
                  <li>
                    <strong translate class="strong black">Active Alerts</strong><strong>:</strong><br>
                    <span translate>A snapshot of the devices that are currently remaining in a Critical, Warning, or Offline state at the end of this reporting period. These represent ongoing issues that have not yet been cleared and require your attention.</span>
                  </li>
                  <li>
                    <strong translate class="strong black">Chart</strong><strong>:</strong><br>
                    <span translate>A line chart tracking your daily Time-At-Risk percentage over the reporting period, compared against a rolling weekly average baseline (Wk Avg.).</span>
                  </li>
                </ol>
              </div>
              <div class="img">
                <img class="img-fluid" src="../../assets/images/help/help-smartPowerReport-3.png">
              </div>
            </div>
            <div class="list" #h_SmartPowerReport_Load>
              <h5 translate>Load Distribution</h5>
              <div class="section">
                <ul>
                  <li translate class="style_none">Analyzes the power capacity of your equipment to help prevent overload-induced downtime.</li>
                </ul>
                <ol>
                  <li>
                    <strong translate class="strong black">Load Approaching Peak Capacity</strong><strong>:</strong><br>
                    <span translate>Highlights the number of reporting devices where the peak load exceeded 70%. These devices are running with limited buffer capacity and may require immediate attention, load shedding, or hardware upgrades to prevent power failure during anomalies.</span>
                  </li>
                  <li>
                    <strong translate class="strong black">Fleet Utilization</strong><strong>:</strong><br>
                    <strong translate>Evaluates the overall utilization health of your UPS fleet based on the total average load. Statuses are categorized to help you quickly assess capacity risks</strong><br>
                  <div class="section">
                    <ul>
                      <li translate>Base (≤ 40%): Healthy load levels with sufficient buffer for power anomalies or future expansion.</li>
                      <li translate>Mid (40% - 70%): Moderate load levels; regular monitoring is recommended.</li>
                      <li translate>High (> 70%): A heavily burdened fleet that may compromise your infrastructure.</li>
                    </ul>
                  </div>
                  </li>
                  <li>
                    <strong translate class="strong black">Chart</strong><strong>:</strong><br>
                    <span translate>A comparative bar chart classifying your devices into Base, Mid, and High load tiers. By displaying both Peak Load (solid bars) and Avg. Load (striped bars) side-by-side, this chart reveals the hidden gap between everyday operations and maximum power demands, making it easy to spot how many devices are creeping into the "High" risk zone.</span>
                  </li>
                </ol>
              </div>
              <div class="img">
                <img class="img-fluid" src="../../assets/images/help/help-smartPowerReport-4.png">
              </div>
            </div>
            <div class="list" #h_SmartPowerReport_Profile>
              <h5 translate>Estimated Consumption Profile</h5>
              <div class="section">
                <ul>
                  <li translate class="style_none">Provides ESG-aligned insights into your IT infrastructure's estimated consumption and carbon footprint.</li>
                </ul>
                <ol>
                  <li>
                    <strong translate class="strong black">Operational Consumption Estimate</strong><strong>:</strong><br>
                    <span translate>The estimated total kWh consumed, calculated based on average load data. (Values are estimated for trend analysis only and may differ from billing.)</span>
                  </li>
                  <li>
                    <strong translate class="strong black">Weekly Trend</strong><strong>:</strong><br>
                    <span translate>The increase or decrease in estimated power consumption compared to the previous period.</span>
                  </li>
                  <li>
                    <strong translate class="strong black">Carbon Footprint</strong><strong>:</strong><br>
                    <span translate>Converts the estimated power consumption into greenhouse gas emissions (kg CO2e).</span>
                  </li>
                  <li>
                    <strong translate class="strong black">Chart</strong><strong>:</strong><br>
                    <span translate>An area chart illustrating your estimated daily power consumption (in kWh) alongside your historical weekly average. This makes it easy to spot abnormal usage spikes or verify the saving impact of shutting down non-critical equipment.</span>
                  </li>
                </ol>
              </div>
              <div class="img">
                <img class="img-fluid" src="../../assets/images/help/help-smartPowerReport-5.png">
              </div>
            </div>
          </section>
        </div>

      </div>
    </div>
  </div>
</main>
